import { useEffect, useRef, useState } from 'react'
import { match } from 'ts-pattern'
import { z } from 'zod'
import type { CoverageStatus } from '../../../utils/api-client/api-schemas.ts'
import { beaconColors } from '../beacon-palette.ts'
import { BeaconCoverageDetail } from './beacon-coverage-detail.tsx'
import * as styles from './beacon-coverage.css.ts'
import { type CoverageModel, depthOf, type EntryPointEntity } from './coverage-model.ts'
import { entryPointKeyOf, selectableProps } from './selectable.ts'
import { squarify } from './treemap-layout.ts'

export const DEPTH_VARIANTS = ['d0', 'd1', 'd2', 'd3', 'd4'] as const

export const MapSizingSchema = z.enum(['operations', 'effort'])
export type MapSizing = z.infer<typeof MapSizingSchema>

/** How much of an asset counts as examined when no operation reaches it, from its own status. */
const statusShare = (status: CoverageStatus): number =>
  match(status)
    .with('examined', () => 1)
    .with('partly', () => 0.5)
    .with('not_examined', 'not_applicable', () => 0)
    .exhaustive()

const truncate = (text: string, length: number) =>
  text.length > length ? `${text.slice(0, Math.max(1, length - 1))}…` : text

/** The rendered width of an element, tracked as it resizes; `fallback` until the first measurement. */
const useElementWidth = (fallback: number) => {
  const ref = useRef<HTMLDivElement>(null)
  const [width, setWidth] = useState(fallback)
  useEffect(() => {
    const element = ref.current
    if (!element) {
      return
    }
    const measure = () => setWidth(element.clientWidth || fallback)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [fallback])
  return [ref, width] as const
}

export const CoverageHatch: React.FC<{ id?: string }> = ({ id = 'beacon-coverage-hatch' }) => (
  <defs>
    <pattern id={id} width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
      <rect width="7" height="7" className={styles.hatchBackground} />
      <line x1="0" y1="0" x2="0" y2="7" className={styles.hatchStripe} />
    </pattern>
  </defs>
)

export type BeaconCoverageMapProps = {
  model: CoverageModel
  sizing: MapSizing
  selectedKey?: string
  onSelect: (key: string) => unknown
}

/**
 * The attack surface as a treemap: entry points hold their operation groups, which hold their
 * operations, each shaded by how deeply it was examined and hatched where it was not.
 */
export const BeaconCoverageMap: React.FC<BeaconCoverageMapProps> = ({ model, sizing, selectedKey, onSelect }) => (
  <>
    <div className={styles.legend}>
      <span>Examination depth (tokens spent per operation, by quartile):</span>
      <span className={styles.legendItem}>
        <i className={styles.swatch} style={{ background: styles.DEPTH_1 }} />
        light
      </span>
      <i className={styles.swatch} style={{ background: styles.DEPTH_2 }} />
      <i className={styles.swatch} style={{ background: styles.DEPTH_3 }} />
      <span className={styles.legendItem}>
        <i className={styles.swatch} style={{ background: styles.DEPTH_4 }} />
        deep
      </span>
      <span className={styles.legendItem}>
        <i className={styles.swatch} style={{ background: styles.HATCH_FILL }} />
        Not examined
      </span>
      <span className={styles.legendItem}>
        <i className={styles.swatch} style={{ background: beaconColors.finding, borderRadius: '50%' }} />
        Verified finding
      </span>
      <span className={styles.muted}>Area = {sizing === 'operations' ? 'one operation each' : 'tokens spent'}</span>
    </div>
    <div className={styles.mapGrid}>
      <Treemap model={model} sizing={sizing} selectedKey={selectedKey} onSelect={onSelect} />
      <aside className={styles.detail} aria-label="Selected part">
        <BeaconCoverageDetail model={model} entityKey={selectedKey} />
      </aside>
    </div>
    <p className={styles.subHeading}>Sensitive assets · share of the operations reaching each one that were examined</p>
    <div className={styles.assets}>
      {model.assets.map(asset => {
        const reaching = asset.operationKeys.flatMap(key => model.operations.get(key) ?? [])
        const examined = reaching.filter(operation => operation.status === 'examined').length
        const share = reaching.length ? examined / reaching.length : statusShare(asset.status)
        return (
          <button
            key={asset.key}
            type="button"
            className={styles.assetCard}
            data-key={asset.key}
            onClick={() => onSelect(asset.key)}
          >
            <span className={styles.assetName}>{asset.name}</span>
            <span className={styles.assetBar}>
              <i className={styles.assetBarFill} style={{ width: `${share * 100}%` }} />
            </span>
            <span className={styles.assetMeta}>
              <span>
                {reaching.length ? `${examined}/${reaching.length} ops examined` : asset.reason || asset.status}
              </span>
              {asset.findings.length > 0 && <b className={styles.assetKept}>● {asset.findings.length}</b>}
            </span>
          </button>
        )
      })}
    </div>
  </>
)

const Treemap: React.FC<BeaconCoverageMapProps> = ({ model, sizing, selectedKey, onSelect }) => {
  const selectedEntryPoint = entryPointKeyOf(model, selectedKey)
  const [ref, width] = useElementWidth(800)
  const height = Math.round(Math.max(360, Math.min(620, width * 0.62)))
  const operationValue = (key: string) => {
    const operation = model.operations.get(key)
    return sizing === 'operations' ? 1 : Math.max(1, operation?.tokens ?? 1)
  }
  const totalValue = [...model.operations.keys()].reduce((total, key) => total + operationValue(key), 0)
  const entryTiles = squarify<EntryPointEntity>(
    model.entryPoints.map(ep => ({
      node: ep,
      // An entry point with nothing to invoke still gets a sliver, so its absence is visible.
      value: ep.operationKeys.length
        ? ep.operationKeys.reduce((total, key) => total + operationValue(key), 0)
        : totalValue * 0.035,
    })),
    { x: 0, y: 0, w: width, h: height }
  )

  const shapes: React.ReactNode[] = []
  const labels: React.ReactNode[] = []
  for (const tile of entryTiles) {
    const ep = tile.node
    const head = tile.w > 80 && tile.h > 44 ? 20 : 0
    const room = Math.floor((tile.w - 14) / 6.8)
    shapes.push(
      <rect
        key={ep.key}
        className={styles.entryTile({ isSelected: selectedKey === ep.key })}
        x={tile.x + 1.5}
        y={tile.y + 1.5}
        width={Math.max(0, tile.w - 3)}
        height={Math.max(0, tile.h - 3)}
        data-key={ep.key}
        tabIndex={0}
        aria-label={`${ep.name}: ${ep.status.replace('_', ' ')}`}
        onClick={() => onSelect(ep.key)}
        onKeyDown={event => (event.key === 'Enter' || event.key === ' ') && onSelect(ep.key)}
      />
    )
    if (ep.operationKeys.length === 0) {
      shapes.push(
        <rect
          key={`${ep.key}:hatch`}
          className={styles.operationTile({ depth: 'd0', isSelected: false })}
          x={tile.x + 4}
          y={tile.y + 4 + head}
          width={Math.max(0, tile.w - 8)}
          height={Math.max(0, tile.h - 8 - head)}
          pointerEvents="none"
        />
      )
      if (head) {
        labels.push(
          <text
            key={`${ep.key}:label`}
            className={`${styles.entryLabel} ${styles.halo}`}
            x={tile.x + 7}
            y={tile.y + 15}
          >
            {truncate(ep.name, room)}
            <tspan className={styles.entryMeta} x={tile.x + 7} dy={16}>
              {tile.w > 190 ? 'not examined · no operations' : 'not examined'}
            </tspan>
          </text>
        )
      }
      continue
    }
    if (head) {
      const meta = `${ep.operationKeys.length} ops${ep.findings.length ? ` · ${ep.findings.length} found` : ''}`
      const hasRoomForMeta = room >= meta.length + 6
      labels.push(
        <text key={`${ep.key}:label`} className={`${styles.entryLabel} ${styles.halo}`} x={tile.x + 7} y={tile.y + 16}>
          {truncate(ep.name, hasRoomForMeta ? Math.max(4, room - meta.length - 1) : room)}
          {hasRoomForMeta && <tspan className={styles.entryMeta}> {meta}</tspan>}
        </text>
      )
    }
    const groupTiles = squarify(
      ep.groupKeys.flatMap(key => {
        const group = model.groups.get(key)
        return group
          ? [{ node: group, value: group.operationKeys.reduce((total, opKey) => total + operationValue(opKey), 0) }]
          : []
      }),
      { x: tile.x + 4, y: tile.y + 4 + head, w: tile.w - 8, h: tile.h - 8 - head }
    )
    for (const groupTile of groupTiles) {
      const group = groupTile.node
      const operationTiles = squarify(
        group.operationKeys.flatMap(key => {
          const operation = model.operations.get(key)
          return operation ? [{ node: operation, value: operationValue(key) }] : []
        }),
        { x: groupTile.x + 1, y: groupTile.y + 1, w: groupTile.w - 2, h: groupTile.h - 2 }
      )
      for (const operationTile of operationTiles) {
        const operation = operationTile.node
        shapes.push(
          <rect
            key={operation.key}
            className={styles.operationTile({
              depth: DEPTH_VARIANTS[depthOf(model, operation)] ?? 'd0',
              isSelected: selectedKey === operation.key,
            })}
            x={operationTile.x}
            y={operationTile.y}
            width={Math.max(0, operationTile.w)}
            height={Math.max(0, operationTile.h)}
            {...selectableProps(
              operation.key,
              `${operation.name}: ${operation.status.replace('_', ' ')}`,
              onSelect,
              operation.entryPointKey === selectedEntryPoint
            )}
          />
        )
        if (operation.findings.length > 0) {
          shapes.push(
            <circle
              key={`${operation.key}:finding`}
              className={styles.findingDot}
              cx={operationTile.x + operationTile.w / 2}
              cy={operationTile.y + operationTile.h / 2}
              r={Math.max(2.2, Math.min(5, Math.min(operationTile.w, operationTile.h) / 3))}
            />
          )
        }
      }
      shapes.push(
        <rect
          key={`${group.key}:outline`}
          className={styles.groupOutline}
          x={groupTile.x}
          y={groupTile.y}
          width={Math.max(0, groupTile.w)}
          height={Math.max(0, groupTile.h)}
        />
      )
      if (group.ownFindings.length > 0 && groupTile.w > 14 && groupTile.h > 14) {
        shapes.push(
          <g key={`${group.key}:own`} pointerEvents="none">
            <circle className={styles.findingDot} cx={groupTile.x + groupTile.w - 9} cy={groupTile.y + 9} r={7} />
            <text
              className={styles.findingCountText}
              x={groupTile.x + groupTile.w - 9}
              y={groupTile.y + 12.5}
              textAnchor="middle"
            >
              {group.ownFindings.length}
            </text>
          </g>
        )
      }
      if (groupTile.w > 110 && groupTile.h > 30) {
        labels.push(
          <text
            key={`${group.key}:label`}
            className={`${styles.groupLabel} ${styles.halo}`}
            x={groupTile.x + 5}
            y={groupTile.y + 14}
          >
            {truncate(group.name, Math.floor(groupTile.w / 6))}
          </text>
        )
      }
    }
  }

  return (
    <div ref={ref} className={styles.treemap}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className={styles.treemapSvg}
        role="group"
        aria-label="Treemap of the attack surface: entry points, operation groups and operations, shaded by how deeply each was examined"
      >
        <CoverageHatch />
        {shapes}
        {labels}
      </svg>
    </div>
  )
}
